import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Typography, Alert, Divider, Tag } from 'antd';
import { wechatPayApi } from '../../services/api';

const { Title } = Typography;
const { TextArea } = Input;

const MASK_PRIVATE_KEY = '__KEEP_PRIVATE_KEY__';
const MASK_APIV3_KEY = '__KEEP_APIV3_KEY__';
const MASK_BRAND_PRIVATE_KEY = '__KEEP_BRAND_PRIVATE_KEY__';
const MASK_BRAND_API_KEY = '__KEEP_BRAND_API_KEY__';

const WechatPayConfig = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await wechatPayApi.getConfig();
      const cfg = res.data;
      if (cfg) {
        setConfigured(true);
        form.setFieldsValue({
          spMchid: cfg.sp_mchid,
          subMchid: cfg.sub_mchid,
          appid: cfg.appid,
          brandId: cfg.brand_id,
          brandDisplayName: cfg.brand_display_name,
          mchCertSerial: cfg.mch_cert_serial,
          mchPrivateKey: cfg.mch_private_key,
          apiv3Key: cfg.apiv3_key,
          servicePhone: cfg.service_phone,
          remark: cfg.remark,
          brandCertSerial: cfg.brand_cert_serial,
          brandPrivateKey: cfg.brand_private_key,
          brandPublicKeyId: cfg.brand_public_key_id,
          brandApiKey: cfg.brand_api_key,
        });
      }
    } catch (err) {
      message.error(err.message || '加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const res = await wechatPayApi.saveConfig(values);
      message.success(res.message || '保存成功');
      // 重新加载，让敏感字段显示为掩码状态
      fetchConfig();
    } catch (err) {
      message.error(err.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <Title level={4}>微信支付配置</Title>
      <p style={{ color: '#999', fontSize: 13 }}>
        配置微信支付服务商（APIv3）所需的商户信息，用于会员卡等微信支付功能。配置仅本公司的管理员可见。
      </p>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="以下信息需要从微信支付商户平台获取"
        description={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>服务商商户号：商户平台「账户中心 → 商户信息」</li>
            <li>商户API证书序列号 + 私钥：商户平台「账户中心 → API安全」申请 API 证书后，在 apiclient_cert 目录中获得 apiclient_key.pem 和证书序列号</li>
            <li>APIv3密钥：商户平台「账户中心 → API安全」设置（32位）</li>
            <li>品牌ID：进驻微信支付品牌商家后获得（灰度期间需联系微信支付运营）</li>
          </ul>
        }
      />

      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Divider orientation="left" orientationMargin={0}>商户信息</Divider>

          <Form.Item
            name="spMchid"
            label="服务商商户号"
            rules={[{ required: true, message: '请输入服务商商户号' }]}
          >
            <Input placeholder="如 1900000001" />
          </Form.Item>

          <Form.Item name="subMchid" label="子商户号（特约商户，如有）">
            <Input placeholder="如 1900000109，无则留空" />
          </Form.Item>

          <Form.Item
            name="appid"
            label="服务号 AppID"
            rules={[{ required: true, message: '请输入服务号AppID' }]}
            extra="会员卡要求使用服务号AppID（不支持App、小程序、订阅号），且需与品牌有B-A关系"
          >
            <Input placeholder="如 wx8888888888888888" />
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>品牌信息（会员卡需要）</Divider>

          <Form.Item
            name="brandId"
            label="品牌ID"
            rules={[{ required: true, message: '请输入品牌ID' }]}
          >
            <Input placeholder="进驻微信支付品牌商家后获得" />
          </Form.Item>

          <Form.Item
            name="brandDisplayName"
            label="品牌展示名称"
            rules={[{ required: true, message: '请输入品牌展示名称' }]}
            extra="展示在会员卡面上，最长10个中文字"
          >
            <Input placeholder="如 和田玉" maxLength={10} showCount />
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>品牌API凭证（会员卡必需，在品牌经营平台获取）</Divider>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="品牌API凭证与APIv3商户证书不共用，须登录品牌经营平台（账号管理→安全中心）重新申请"
          />

          <Form.Item
            name="brandCertSerial"
            label="品牌API证书序列号"
            rules={[{ required: true, message: '请输入品牌API证书序列号' }]}
          >
            <Input placeholder="申请品牌API证书后获得" />
          </Form.Item>

          <Form.Item
            name="brandPrivateKey"
            label="品牌API证书私钥（apiclient_key.pem 内容）"
            rules={[{ required: true, message: '请粘贴品牌API证书私钥' }]}
            extra={configured ? (
              <span>已配置。{<Tag color="green" style={{ marginLeft: 4 }}>保存过，可不重新填写</Tag>}粘贴新内容将覆盖旧私钥</span>
            ) : '品牌经营平台申请品牌API证书后下载的 apiclient_key.pem 完整内容'}
          >
            <TextArea
              rows={5}
              placeholder={'-----BEGIN PRIVATE KEY-----\nMIIEvgIBADANBg...\n-----END PRIVATE KEY-----'}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>

          <Form.Item
            name="brandPublicKeyId"
            label="微信支付公钥ID"
            rules={[{ required: true, message: '请输入微信支付公钥ID' }]}
            extra="品牌经营平台申请微信支付公钥后显示的公钥ID，以 PUB_KEY_ID_ 开头"
          >
            <Input placeholder="如 PUB_KEY_ID_16529668179687..." />
          </Form.Item>

          <Form.Item
            name="brandApiKey"
            label="品牌API密钥（用于接收开卡回调通知）"
            rules={[{ required: true, message: '请输入品牌API密钥' }]}
            extra="品牌经营平台设置的32位API密钥，用于解密微信推送的开卡等回调通知"
          >
            <Input.Password placeholder="32位密钥" />
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>API 安全（APIv3 签名）</Divider>

          <Form.Item
            name="mchCertSerial"
            label="商户API证书序列号"
            rules={[{ required: true, message: '请输入证书序列号' }]}
          >
            <Input placeholder="申请API证书后获得，如 5157F09EFDC096DE15EBE81A47057A72..." />
          </Form.Item>

          <Form.Item
            name="mchPrivateKey"
            label="商户API私钥（apiclient_key.pem 内容）"
            rules={[{ required: true, message: '请粘贴商户API私钥' }]}
            extra={configured ? (
              <span>已配置。{<Tag color="green" style={{ marginLeft: 4 }}>保存过，可不重新填写</Tag>}粘贴新内容将覆盖旧私钥</span>
            ) : '粘贴 apiclient_key.pem 文件的完整内容（含 BEGIN/END PRIVATE KEY 行）'}
          >
            <TextArea
              rows={5}
              placeholder={'-----BEGIN PRIVATE KEY-----\nMIIEvgIBADANBg...\n-----END PRIVATE KEY-----'}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>

          <Form.Item
            name="apiv3Key"
            label="APIv3密钥"
            rules={[{ required: true, message: '请输入APIv3密钥' }]}
            extra={configured ? '已配置。留空或保持不变则不修改' : '商户平台设置的32位APIv3密钥'}
          >
            <Input.Password placeholder="32位密钥" />
          </Form.Item>

          <Divider orientation="left" orientationMargin={0}>其他</Divider>

          <Form.Item name="servicePhone" label="会员卡服务电话">
            <Input placeholder="展示在会员卡详情内，建议填固定电话" />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <Input placeholder="可选" />
          </Form.Item>

          <Divider />

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving} style={{ background: '#8B7355' }}>
              保存配置
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default WechatPayConfig;
